<template>
    <div>
        <div class="stepper-title">{{$t(stepTitle)}}</div>
        <v-stepper
            ref="stepper"
            v-model="currentIndex"
            alt-labels
            class="transparent"
        >
            <v-stepper-header>
                <template v-for="(step, index) in steps">
                    <v-stepper-step :key="`step-${index}`" :complete="step.isComplete" :step="index" color="secondary">
                        {{ $t(step.title) }}
                    </v-stepper-step>
                    <v-divider v-if="index < lastStepIndex" :key="`div-${index}`"></v-divider>
                </template>
            </v-stepper-header>
            <v-stepper-items>
                <slot />
            </v-stepper-items>
        </v-stepper>
    </div>
</template>

<script>
    import { applicationState } from "@/state"
    import StepId from "@/model/progress/StepId"
    import StepsByMode from "@/model/progress/Steps"
    import StepMode, { fromPrimaryLenderApplyMode } from "@/model/progress/StepMode"

    export default {
        name: "Stepper",
        data() {
            return {
                currentIndex: 0,
                steps: this.getSteps(),
                stepTitle:""
            }
        },

        computed: {
            lastStepIndex() {
                return this.steps.length - 1
            },
        },

        watch: {
            $route() {
                this.steps = this.getSteps()
                const stepIndex = this.steps.findIndex(step => step.id === this.$route.meta?.step)
                this.currentIndex = Math.max(0, stepIndex)

                const step = this.steps[this.currentIndex]
                if (step) {
                    this.stepTitle = step.title
                }

                this.completeSteps()
                // In certain cases, the active step doesn't render as active, so we call updateView on the component
                setTimeout(() => this.$refs?.stepper?.updateView())
            },
        },

        mounted() {
            this.updateStyles();
            window.addEventListener('resize', this.updateStyles);
        },

        beforeDestroy() {
            window.removeEventListener('resize', this.updateStyles);
        },

        methods: {
            getSteps() {
                if (this.$route.name === "nooffer") {
                    return StepsByMode[StepMode.NO_OFFER]
                }

                if (applicationState.application.jointEnabled) {
                    return StepsByMode[StepMode.FULL_JOINT]
                }

                return StepsByMode[fromPrimaryLenderApplyMode(applicationState.primaryLenderApplyMode)]
            },

            completeSteps() {
                this.steps.forEach((step, index) => {
                    step.isComplete = this.currentIndex > index
                    if (step.id === StepId.COMPLETE && this.currentIndex === index) {
                        step.isComplete = true
                    }
                })
            },

            updateStyles() {
                let steps = document.getElementsByClassName("v-stepper__step__step")

                if(steps?.length > 0) {
                    if (window.innerWidth < 959) {
                        steps[0].style.borderRadius = "24px 1px 1px 24px"
                        steps[steps.length - 1].style.borderRadius = "1px 24px 24px 1px"
                    } else {
                        steps[0].style.borderRadius = "50%"
                        steps[steps.length - 1].style.borderRadius = "50%"
                    }
                }
            },
        },
    }
</script>

<style lang="scss">
@function get-vw($target) {
    $vw-context: (959*.01) * 1px;
    @return ($target/$vw-context) * 1vw;
}

$stepper-padding: clamp(.25rem, 1vw, 4rem);
$calc-size: get-vw(100px);

    .stepper-title {
        display: none;
    }

    .v-stepper {
        background: transparent !important;
    }

    .v-stepper--alt-labels .v-stepper__header {
        max-width: 100%;
        height: auto;
        margin: auto;
    }

    .v-stepper--alt-labels .v-stepper__step {
        align-items: flex-start !important;
        transform: translateX(59px);
        font-size: 14px;
    }

    .v-stepper .v-stepper__step__step .v-icon {
        color: var(--v-primary-base) !important;
    }

    .v-stepper__step--complete > .v-stepper__step__step.secondary {
        background-color: transparent !important;
        border-color: transparent !important;
    }

    // Make stepper responsive for smaller screens
    @media only screen and (max-width: 959px) {

        //Hide the label
        .v-stepper__label {
            display: none !important;
        }

        .stepper-title{
            display: flex;
            justify-content: center;
            align-items: center;
            margin-top: 20px;
            font-size: 20px;
            font-weight: bold;
        }

        .v-stepper__header {
            display: flex;
            flex-wrap: nowrap;
            justify-content: center;

            .v-divider {
                display: none;
                transform: translateX(-24px);
            }
        }

        .v-stepper__step {
            padding: 16px $stepper-padding !important;
        }

        // Turning circles into logs
        .v-stepper__step__step {
            width: $calc-size;
            border-radius: 0;
        }

        .v-stepper--alt-labels .v-stepper__step {
            // remove flex bias
            flex-basis: 0;
            transform: none;
        }

        .v-stepper:not(.v-stepper--vertical) .v-stepper__label {
            display: none !important;
        }

        // Cover up when step is checked
        .v-stepper__step--complete > .v-stepper__step__step.secondary {
            background-color: var(--v-secondary-base) !important;
            border-color: var(--v-secondary-base) !important;
        }

        // Hide check icon
        .v-stepper .v-stepper__step__step .v-icon {
            color: transparent !important;
        }

    }
</style>
